import { useEffect } from "react";
import { useHistory } from "@iventis/subscriptions";
import { parseDateToEpochSeconds, parseEpochSecondsToDate, parseTimeDateToNumber, parseTimeNumberToDate } from "@iventis/utilities";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../state/root.store";
import { map } from "../state/map.slice";

const DAY_FILTER_PARAM = "day-filter";

const TIME_FILTER_PARAM = "time-filter";

/** Syncs all of the date filters and the state stored in the URL */
export function useDateFilterUrlState() {
    const dispatch: RootDispatch = useDispatch();

    const history = useHistory();

    const { time, day } = useSelector(
        (state: RootState) => ({
            day: state.mapReducer.mapModule.datesFilter.value.day,
            time: state.mapReducer.mapModule.datesFilter.value.time,
            stamp: state.mapReducer.mapModule.datesFilter.stamp,
        }),
        (prev, next) => prev.stamp === next.stamp
    );

    /** Removes the date filters from the URL */
    const clearDateFilters = () => {
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.delete(DAY_FILTER_PARAM);
        searchParams.delete(TIME_FILTER_PARAM);
        history.push({ search: searchParams.toString() });
    };

    useEffect(() => {
        // On page load, check if there are day and time filters in the URL
        const searchParams = new URLSearchParams(window.location.search);
        const dayFilter = searchParams.get(DAY_FILTER_PARAM);
        const timeFilter = searchParams.get(TIME_FILTER_PARAM);

        if (dayFilter != null && timeFilter != null) {
            // Parse the day and time filters to ints
            const dayInt = parseInt(dayFilter, 10);
            const timeInt = parseInt(timeFilter, 10);
            // Check they are valid, if not then remove them from the URL
            if (Number.isNaN(dayInt) || Number.isNaN(timeInt)) {
                clearDateFilters();
            } else {
                // If they are valid filters then update the global state
                dispatch(map.actions.setDateFilter({ day: dayInt, time: timeInt }));
            }
        }
    }, []);

    /** When the time filter param needs to be updated in the URL */
    const updateTimeParam = (time: Date) => {
        const timeNumber = parseTimeDateToNumber(time);

        if (timeNumber != null) {
            const searchParams = new URLSearchParams(window.location.search);

            // If the date filter param has not been set previously in the URL, set it
            if (day != null && !searchParams.has(DAY_FILTER_PARAM)) {
                searchParams.set(DAY_FILTER_PARAM, day.toString());
            }
            // Set the time filter in the URL
            searchParams.set(TIME_FILTER_PARAM, timeNumber.toString());
            history.push({ search: searchParams.toString() });
        }
    };

    /** When the date filter param needs to be updated in the URL */
    const updateDayParam = (date: Date) => {
        const dayNumber = parseDateToEpochSeconds(date);

        if (dayNumber != null) {
            const searchParams = new URLSearchParams(window.location.search);

            // If the time filter param has not been set previously in the URL, set it
            if (time != null && !searchParams.has(TIME_FILTER_PARAM)) {
                searchParams.set(TIME_FILTER_PARAM, time.toString());
            }
            // Set the day filter in the URL
            searchParams.set(DAY_FILTER_PARAM, dayNumber.toString());
            history.push({ search: searchParams.toString() });
        }
    };

    /** When the date filter is toggled on or off */
    const updateFilter = (filter: boolean) => {
        // If the filter is off, then remove the filter from the URL
        if (!filter) {
            clearDateFilters();
        } else {
            // If filter toggled on we need to set the date and time filters in the URL
            const startingDay = parseEpochSecondsToDate(day) ?? new Date();
            updateDayParam(startingDay);
            const startingTime = parseTimeNumberToDate(time) ?? new Date();
            updateTimeParam(startingTime);
        }
    };

    return { updateDayParam, updateTimeParam, updateFilter };
}
